import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  HttpClient,
  HttpErrorResponse
} from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';

import { LocalStorageService } from '../../shared/service/localstorage/localstorage.service';
import { comumService } from '../../shared/service/comum.service';
import { ToastService } from '../../shared/service/toast.service';

import { AuthContextService } from './auth-context.service';

export interface AuthMePapel {
  codigo: string;
  nome: string;
  papelPai?: string | null;
}

export interface AuthMeRecursoOperacao {
  recurso: string;
  operacoes: string[];
}

export interface AuthMeResponse {
  authenticated: boolean;
  expiresAt?: string;
  renewable?: boolean;

  usuario: {
    cpf: string;
    nome: string;
    nomeSocial?: string | null;
    email: string;
    matricula?: string | null;
  };

  unidade: {
    codigo: string;
    sigla?: string;
    nome: string;
    codigoOrgao?: string | null;
  };

  roles: string[];
  papeis: AuthMePapel[];
  recursosOperacoes: AuthMeRecursoOperacao[];
  fonteAutorizacaoRecursos?: string;

  /*
   * Campo temporário de compatibilidade.
   *
   * Não deve ser usado como contrato principal em novos códigos.
   * O AuthContextService monta esse campo ao salvar o user_info.
   */
  jsonUnidadePapeis?: string;

  /*
   * Campo temporário utilizado por componentes legados.
   */
  name?: string;
}

export interface LogoutResponse {
  loggedOut: boolean;
  logoutUrl?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  public loggedInEmitter: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  private readonly AUTH_METHOD_INVALID =
    'auth_method_invalid';

  private readonly AUTH_METHOD_INVALID_MESSAGE =
    'Método de autenticação inválido. Tente novamente utilizando um certificado digital válido.';

  private erroMetodoAutenticacaoNotificado = false;
  /*
   * Mantém o bloqueio mesmo depois que o Angular remove
   * error=auth_method_invalid da URL.
   */
  private erroMetodoAutenticacaoDetectado = false;

  /*
   * Evita criar mais de um temporizador de redirecionamento.
   */
  private redirecionamentoCertificadoAgendado = false;

  /*
   * Impede vários redirecionamentos simultâneos.
   */
  private redirecionamentoLoginEmAndamento = false;

  /*
   * Impede loop caso o Seg.ID volte a retornar
   * auth_method_invalid mesmo com prompt=login.
   */
  private readonly AUTH_METHOD_INVALID_REDIRECT_KEY =
    'auth_method_invalid_redirecting';

  constructor(
    private router: Router,
    private http: HttpClient,
    private localStorage: LocalStorageService,
    private service: comumService,
    private authContextService: AuthContextService,
    private message: ToastService
  ) { }

  private ehRotaPublica(): boolean {
    const rotasPublicas = [
      '/validacao-autenticidade'
    ];

    const rotaAtual = window.location.hash
      .replace(/^#/, '')
      .split('?')[0];

    return rotasPublicas.includes(rotaAtual);
  }

  /**
   * Verifica se o backend retornou erro informando que
   * o método de autenticação utilizado é inválido.
   *
   * Exemplo:
   * /?error=auth_method_invalid
   */

  public possuiErroMetodoAutenticacao(): boolean {

    /*
     * Depois que o erro foi identificado uma vez,
     * não dependemos mais da URL.
     */
    if (this.erroMetodoAutenticacaoDetectado) {
      return true;
    }

    const queryParams =
      new URLSearchParams(window.location.search);

    const hash =
      window.location.hash || '';

    const hashQueryIndex =
      hash.indexOf('?');

    const hashQuery =
      hashQueryIndex >= 0
        ? hash.substring(hashQueryIndex + 1)
        : '';

    const hashParams =
      new URLSearchParams(hashQuery);

    const erro =
      queryParams.get('error') ||
      hashParams.get('error');

    if (erro === this.AUTH_METHOD_INVALID) {
      this.erroMetodoAutenticacaoDetectado = true;

      return true;
    }

    return false;
  }

  /**
 * Exibe a mensagem de erro, bloqueia o fluxo normal
 * e inicia uma única nova autenticação com prompt=login.
 */
  private tratarErroMetodoAutenticacao(): boolean {
    if (!this.possuiErroMetodoAutenticacao()) {
      this.erroMetodoAutenticacaoNotificado = false;
      return false;
    }

    this.loggedInEmitter.emit(false);

    /*
    * Não executar router.initialNavigation() neste cenário.
    * Essa chamada leva o Angular para #/ e deixa a tela branca.
    */

    this.authContextService.limparContexto();

    localStorage.removeItem('papeis_usuario');
    localStorage.removeItem('termo');
    sessionStorage.removeItem('loginRegistrado');

    if (!this.erroMetodoAutenticacaoNotificado) {
      this.erroMetodoAutenticacaoNotificado = true;

      this.message.showError(
        this.AUTH_METHOD_INVALID_MESSAGE
      );
    }

    this.agendarLoginComCertificado();

    return true;
  }

  /**
* Agenda somente uma nova tentativa de autenticação.
*
* A chave do sessionStorage impede loop caso o Seg.ID
* retorne novamente auth_method_invalid.
*/
  private agendarLoginComCertificado(): void {
    if (
      this.redirecionamentoCertificadoAgendado ||
      this.redirecionamentoLoginEmAndamento
    ) {
      return;
    }

    const redirecionamentoJaRealizado =
      sessionStorage.getItem(
        this.AUTH_METHOD_INVALID_REDIRECT_KEY
      );

    if (redirecionamentoJaRealizado === 'true') {
      console.warn(
        '[auth] Redirecionamento com prompt=login já realizado.'
      );

      return;
    }

    this.redirecionamentoCertificadoAgendado = true;

    sessionStorage.setItem(
      this.AUTH_METHOD_INVALID_REDIRECT_KEY,
      'true'
    );

    console.log(
      '[auth] Redirecionamento para o Seg.ID agendado.'
    );

    setTimeout(() => {
      this.redirecionarParaLoginComCertificado();
    }, 1500);
  }
  /**
   * Solicita ao backend uma nova autenticação, exigindo que
   * o Seg.ID apresente novamente sua tela de login.
   */
  private redirecionarParaLoginComCertificado(): void {
    if (this.redirecionamentoLoginEmAndamento) {
      return;
    }

    this.redirecionamentoLoginEmAndamento = true;

    const loginUrl =
      `${environment.authService}` +
      `/auth/segid/login?prompt=login`;

    console.log(
      '[auth] Redirecionando para:',
      loginUrl
    );

    window.location.replace(loginUrl);
  }

  public async conectar(): Promise<void> {
    /*
     * O backend bloqueou a autenticação antes de criar
     * a sessão do usuário.
     *
     * Nesse cenário:
     * - não consulta /auth/me;
     * - não tenta renovar a sessão;
     * - não inicia novamente o login no Seg.ID.
     */
    if (this.tratarErroMetodoAutenticacao()) {
      return;
    }

    if (this.ehRotaPublica()) {
      this.tratarRotaPublica();
      return;
    }

    try {
      const userInfo =
        await this.buscarContextoAutenticado();

      if (userInfo?.authenticated) {
        await this.aplicarSessaoAutenticada(userInfo);
        return;
      }

      this.tratarSessaoInvalida(true);
    } catch (error: unknown) {
      /*
       * Não tenta refresh quando o backend já informou
       * que o método de autenticação é inválido.
       */
      if (this.tratarErroMetodoAutenticacao()) {
        return;
      }

      const renovou =
        await this.tentarRenovarSessao(error);

      if (!renovou) {
        this.tratarSessaoInvalida(true);
      }
    }
  }

  public async login(): Promise<void> {
    await this.conectar();
  }


  public async logout(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.post<LogoutResponse>(
          `${environment.authService}/auth/logout`,
          {},
          { withCredentials: true }
        )
      );

      if (response?.loggedOut === true && response?.logoutUrl) {
        this.limparContextoLocal();
        const logoutUrlNormalizada = this.normalizarUrlLogout(response.logoutUrl);
        window.location.replace(logoutUrlNormalizada);
        return;
      }

      console.error('[auth] Logout não concluído ou logoutUrl não retornada pelo backend.');
    } catch (error: unknown) {
      console.error('[auth] Não foi possível concluir o logout no backend:', error);
    }
  }


  /**
   * Normaliza somente a rota /login do frontend para o padrão com hash.
   *
   * Aceita:
   * /login
   * /#/login
   * /login?logout=true
   * /#/login?logout=true
   *
   * URLs do backend e do provedor de identidade são mantidas.
   */
  private normalizarUrlLogout(
    logoutUrl: string
  ): string {
    try {
      const url = new URL(
        logoutUrl,
        window.location.origin
      );

      const pathnameNormalizado =
        url.pathname.replace(/\/+$/, '') || '/';

      const hashNormalizado =
        url.hash.replace(/^#/, '');

      /*
       * Já está no padrão esperado:
       * #/login
       * #/login?parametro=valor
       */
      if (
        hashNormalizado === '/login' ||
        hashNormalizado.startsWith('/login?')
      ) {
        return url.toString();
      }

      /*
       * Existe outro hash que não corresponde ao login.
       * Nesse caso, não altera a URL para evitar corromper
       * URLs do backend ou do provedor de identidade.
       */
      if (url.hash) {
        return url.toString();
      }

      /*
       * Apenas a rota exata /login do frontend
       * deve ser convertida para /#/login.
       */
      if (pathnameNormalizado !== '/login') {
        return url.toString();
      }

      const queryString = url.search;

      url.pathname = '/';
      url.search = '';
      url.hash = `/login${queryString}`;

      return url.toString();
    } catch (error: unknown) {
      console.warn(
        '[auth] Não foi possível normalizar a URL de logout:',
        error
      );

      return logoutUrl;
    }
  }

  public async buscarContextoAutenticado():
    Promise<AuthMeResponse> {
    return await firstValueFrom(
      this.http.get<AuthMeResponse>(
        `${environment.authService}/auth/me`,
        {
          withCredentials: true
        }
      )
    );
  }

  public definirContextoAutenticado(
    userInfo: AuthMeResponse
  ): void {
    this.authContextService.definirContexto(userInfo);
  }

  public async renovarSessao(): Promise<boolean> {
    /*
     * Não tenta renovar a sessão quando o login foi
     * bloqueado por método de autenticação inválido.
     */
    if (this.tratarErroMetodoAutenticacao()) {
      return false;
    }

    try {
      await firstValueFrom(
        this.http.post(
          `${environment.authService}/auth/refresh`,
          {},
          {
            withCredentials: true
          }
        )
      );

      const userInfo =
        await this.buscarContextoAutenticado();

      if (userInfo?.authenticated) {
        await this.aplicarSessaoAutenticada(userInfo);
        return true;
      }

      return false;
    } catch {
      this.limparContextoLocal();
      return false;
    }
  }

  public async tratarSessaoExpirada(): Promise<void> {
    /*
     * Impede que um 401 ou 403 reinicie o fluxo
     * automático de autenticação.
     */
    if (this.tratarErroMetodoAutenticacao()) {
      return;
    }

    const renovou = await this.renovarSessao();

    if (!renovou) {
      this.tratarSessaoInvalida(true);
    }
  }

  private async tentarRenovarSessao(
    error: unknown
  ): Promise<boolean> {
    if (this.tratarErroMetodoAutenticacao()) {
      return false;
    }

    if (!this.deveTentarRefresh(error)) {
      return false;
    }

    return await this.renovarSessao();
  }

  private deveTentarRefresh(
    error: unknown
  ): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    return [401, 403].includes(error.status);
  }

  private async aplicarSessaoAutenticada(
    userInfo: AuthMeResponse
  ): Promise<void> {
    /*
     * A autenticação foi concluída com sucesso.
     * Libera o fluxo para os próximos acessos.
     */
    this.erroMetodoAutenticacaoDetectado = false;
    this.erroMetodoAutenticacaoNotificado = false;
    this.redirecionamentoCertificadoAgendado = false;
    this.redirecionamentoLoginEmAndamento = false;

    sessionStorage.removeItem(
      this.AUTH_METHOD_INVALID_REDIRECT_KEY
    );

    this.salvarContextoAutenticado(userInfo);

    this.loggedInEmitter.emit(true);
    this.router.initialNavigation();

    try {
      await this.countLoginRegister();
    } catch (error: unknown) {
      console.warn(
        '[auth] countLoginRegister falhou (best-effort):',
        error
      );
    }
  }

  /**
   * Centraliza o contexto recebido do /auth/me.
   *
   * O AuthContextService:
   * - normaliza os dados;
   * - mantém o contexto em memória;
   * - salva o user_info;
   * - cria name e jsonUnidadePapeis temporariamente.
   */
  private salvarContextoAutenticado(
    userInfo: AuthMeResponse
  ): void {
    this.authContextService.definirContexto(userInfo);
  }
  private limparContextoLocal(): void {
    this.authContextService.limparContexto();

    /*
     * Não usar sessionStorage.clear(), pois isso apagaria
     * a proteção contra loop do auth_method_invalid.
     */
    sessionStorage.removeItem('loginRegistrado');

    localStorage.removeItem('papeis_usuario');
    localStorage.removeItem('termo');
    localStorage.removeItem('loginRegistrado');
  }

  private tratarSessaoInvalida(
    redirecionarLogin: boolean
  ): void {
    this.limparContextoLocal();

    /*
     * O erro permanece na URL e impede que o frontend
     * reinicie o login automaticamente.
     */
    if (this.tratarErroMetodoAutenticacao()) {
      return;
    }

    if (redirecionarLogin) {
      this.iniciarLoginBackend();
    }
  }

  private iniciarLoginBackend(): void {
    /*
     * Última proteção antes do redirecionamento.
     *
     * Mesmo que algum outro ponto do sistema chame este
     * método, o fluxo não reinicia enquanto o parâmetro
     * error=auth_method_invalid estiver presente.
     */
    if (this.tratarErroMetodoAutenticacao()) {
      return;
    }

    window.location.href =
      `${environment.authService}/auth/segid/login`;
  }

  /**
   * Mantém o retorno string[] para não quebrar guard,
   * navbar, home ou outros consumidores atuais.
   *
   * Internamente, os papéis passam a vir do contexto central.
   */
  public hasRole(): string[] {
    const papeis =
      this.authContextService.getCodigosPapeis();

    if (papeis.length > 0) {
      return papeis;
    }

    this.router.navigate(['acesso-negado']);

    return [];
  }

  /**
   * Novo método central para validar um ou mais papéis.
   *
   * Pode ser utilizado gradualmente nos novos consumidores.
   */
  public possuiPapel(
    ...codigosPermitidos: string[]
  ): boolean {
    return this.authContextService.hasPapel(
      ...codigosPermitidos
    );
  }

  /**
   * Verifica roles do contexto normalizado.
   */
  public possuiRole(
    ...rolesPermitidas: string[]
  ): boolean {
    return this.authContextService.hasRole(
      ...rolesPermitidas
    );
  }

  /**
   * Verifica recurso e operação, quando esse contrato
   * estiver habilitado no Seg.ID.
   */
  public possuiRecursoOperacao(
    recurso: string,
    operacao?: string
  ): boolean {
    return this.authContextService.hasRecursoOperacao(
      recurso,
      operacao
    );
  }

  public isAuthenticated(): boolean {
    return this.authContextService.isAuthenticated();
  }

  /**
   * Mantém o método existente para evitar impacto
   * em consumidores atuais.
   */
  public getUserInfo(): AuthMeResponse | null {
    try {
      const userInfoString =
        this.localStorage.get('user_info');

      if (!userInfoString) {
        return null;
      }

      return JSON.parse(
        userInfoString
      ) as AuthMeResponse;
    } catch {
      return null;
    }
  }

  private tratarRotaPublica(): void {
    const url = window.location.href;
    const queryString = url.split('?')[1];

    if (!queryString) {
      return;
    }

    const params =
      new URLSearchParams(queryString);

    this.router.navigate(
      ['/validacao-autenticidade'],
      {
        queryParams: {
          codigo_verificador:
            params.get('codigo_verificador') ?? '',

          codigo_crc:
            params.get('codigo_crc') ?? ''
        }
      }
    );
  }

  public async countLoginRegister() {
    if (
      sessionStorage.getItem('loginRegistrado')
    ) {
      return;
    }

    const userInfo =
      this.authContextService.getContexto();

    if (!userInfo?.usuario?.cpf) {
      console.warn(
        '[auth] Registro de login não realizado: ' +
        'CPF não encontrado no contexto autenticado.'
      );

      return;
    }

    const primeiroPapel =
      userInfo.papeis?.length
        ? userInfo.papeis[0]
        : null;

    const perfilStr =
      primeiroPapel?.codigo ?? null;

    const userInfoObj = {
      cpf: userInfo.usuario.cpf,

      /*
       * PERFIL_SSC possui limite máximo de 100 caracteres.
       * substring limita o tamanho sem fazer preenchimento mínimo.
       */
      perfil_ssc: perfilStr
        ? perfilStr.substring(0, 100)
        : null,

      data_login: new Date(),

      ip: await this.getExternalIP(),

      user_agent: navigator.userAgent
    };

    this.service
      .post(
        userInfoObj,
        '/estatistica/registro-login'
      )
      .subscribe({
        next: () => {
          sessionStorage.setItem(
            'loginRegistrado',
            'true'
          );
        },

        error: (error: unknown) => {
          console.error(
            'Erro ao registrar login:',
            error
          );
        }
      });

    return userInfoObj;
  }

  public async getExternalIP():
    Promise<string | null> {
    try {
      const response = await fetch(
        'https://api.ipify.org?format=json'
      );

      if (!response.ok) {
        throw new Error(
          'Falha ao obter IP'
        );
      }

      const data = await response.json() as {
        ip?: string;
      };

      return data.ip || null;
    } catch (error: unknown) {
      console.warn(
        'Não foi possível obter o IP externo:',
        error
      );

      return null;
    }
  }
}